import type { CareplanOrderLine } from "@/domains/careplan/models/types";
import { SUBSCRIPTION_STATUS } from "@/domains/subscriptions/models/constants";
import type { SubscriptionStatus } from "@/domains/subscriptions/models/types";

/** What the product card's cart link does when pressed. */
export type CartLinkIntent = "view-cart" | "restore" | "none";

type CartLinkCopy = {
  /** Plain-weight lead-in: "Subscribed." Null when the action says it all. */
  prefix: string | null;
  /** Bold action, or null when there is nothing to do. */
  action: string | null;
};

export type CartLinkState = CartLinkCopy & {
  intent: CartLinkIntent;
  /*
    One-line form for the desktop price row, which has about 250px beside the price at the
    card's widest. Subscription states drop the prefix and let the action carry the status.
  */
  compact: CartLinkCopy;
};

type GetCartLinkStateOptions = {
  line: CareplanOrderLine;
  subscriptionStatus: SubscriptionStatus | null;
  /** Right after checkout, while the subscription is being created. */
  isProcessing: boolean;
};

/*
  The link reads the same way the cart does for that state: "Your Subscription" with Manage
  Subscription, "Your subscription is paused" with Resume, "You don't have any active
  subscriptions" with reactivation, "Your New Subscription" with Update Subscription.
*/
export const getCartLinkState = ({
  isProcessing,
  line,
  subscriptionStatus,
}: GetCartLinkStateOptions): CartLinkState => {
  if (isProcessing) {
    return {
      prefix: "Order processing.",
      action: null,
      intent: "none",
      compact: { prefix: "Order processing.", action: null },
    };
  }

  if (line.subscriptionProduct) {
    const { quantity } = line.subscriptionProduct;

    switch (subscriptionStatus) {
      case SUBSCRIPTION_STATUS.ACTIVE:
        return {
          prefix: quantity > 1 ? `Subscribed, ${quantity} a month.` : "Subscribed.",
          action: "Manage Subscription",
          intent: "view-cart",
          compact: { prefix: null, action: "Manage Subscription" },
        };
      case SUBSCRIPTION_STATUS.PAUSED:
        return {
          prefix: "Subscription paused.",
          action: "Resume",
          intent: "view-cart",
          compact: { prefix: null, action: "Resume Subscription" },
        };
      case SUBSCRIPTION_STATUS.PENDING:
      case SUBSCRIPTION_STATUS.HAS_RECOMMENDATIONS:
        return {
          prefix: "Subscription update pending.",
          action: "View Cart",
          intent: "view-cart",
          compact: { prefix: "Update pending.", action: "View Cart" },
        };
      case SUBSCRIPTION_STATUS.CANCELLED:
      case SUBSCRIPTION_STATUS.EXPIRED:
      case SUBSCRIPTION_STATUS.INACTIVE:
        return {
          prefix: "Subscription ended.",
          action: "Reactivate",
          intent: "view-cart",
          compact: { prefix: null, action: "Reactivate Subscription" },
        };
      default:
        return {
          prefix: "Subscribed.",
          action: "View Cart",
          intent: "view-cart",
          compact: { prefix: "Subscribed.", action: "View Cart" },
        };
    }
  }

  if (!line.isActive) {
    return {
      prefix: "Not in your order.",
      action: "Add to Cart",
      intent: "restore",
      /* "Not in your order." does not fit beside the price at 16px; "Removed." says the same. */
      compact: { prefix: "Removed.", action: "Add to Cart" },
    };
  }

  const hasSubscription =
    subscriptionStatus !== null && subscriptionStatus !== SUBSCRIPTION_STATUS.NO_SUBSCRIPTION;

  if (hasSubscription) {
    return {
      prefix: "New to your plan.",
      action: "Update Subscription",
      intent: "view-cart",
      compact: { prefix: null, action: "Update Subscription" },
    };
  }

  /* The drawer is where quantities change, so the link names what the patient can do there. */
  return {
    prefix: null,
    action: "Remove or Edit Quantity",
    intent: "view-cart",
    compact: { prefix: null, action: "Remove or Edit Quantity" },
  };
};
